import { eventToArtifact } from "../utils/eventToArtifact";
import { exportNcrDocument } from "../utils/exportNcrDocument";


const GAP_LABELS = {
  missing_root_cause:
    "Root cause analysis has not yet been documented.",
  missing_corrective_action:
    "Corrective action has not yet been defined.",
  missing_operator:
    "Operator details have not been identified.",
  missing_disposition:
    "Disposition decisions have not been documented.",
};

const GAP_TITLES = {
  missing_root_cause:
    "Missing Root Cause Analysis",
  missing_corrective_action:
    "Missing Corrective Action",
  missing_operator:
    "Missing Operator details",
  missing_disposition:
    "Missing disposition details",
};

export default function EventDetail({ event }) {

  if (!event) {
    return (
      <div className="bg-white border rounded-xl p-6">
        Select an operational event
      </div>
    );
  }

  const transcript =
    event.transcript ||
    event.payload?.transcript ||
    "No transcript available";

  const evidenceImages =
    event.payload?.evidenceImages || [];

  const clauses =
    event.clauses || [];

  const complianceSignals =
    event.complianceSignals || [];

  const checklist = event.checklist || {
    required_fields: [],
    completed_fields: []
  };

  const suggestedActions =
    event.suggested_actions || [];

  return (

    <div className="bg-white border rounded-xl p-6 shadow-sm">

      {/* Header */}

      <div className="flex items-start justify-between">

        <div>

          <div className="text-2xl font-bold">
            Operational Event
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {clauses.map((clause, idx) => (
              <div
                key={idx}
                className="
                  px-3
                  py-1
                  bg-red-100
                  text-red-700
                  rounded-full
                  text-sm
                "
              >
                AS9100 {clause}
              </div>
            ))}
          </div>
        </div>
        <div className="text-sm text-gray-500">
          {complianceSignals.length} compliance signal(s)
        </div>
      </div>

      {/* Transcript */}

      <div className="mt-6">

        <div className="font-semibold mb-2">
          Operational Observation
        </div>

        {/* Audio Playback */}
        {event.payload?.audioBase64 && (
            <div className="mt-6">
              <div className="font-semibold mb-2">
                Original Audio Capture
              </div>
              <audio
                controls
                className="w-full"
              >
                <source
                  src={event.payload.audioBase64}
                />
              </audio>
            </div>
          )}

        <div className="bg-gray-50 rounded-lg p-4 text-gray-700 leading-relaxed">
          {transcript}
        </div>

      </div>

      {/* Evidence */}

      <div className="mt-6">

        <div className="font-semibold mb-3">
          Linked Evidence
        </div>

        {evidenceImages.length > 0 ? (

          <div className="grid grid-cols-3 gap-4">

            {evidenceImages.map((img, idx) => (

              <div
                key={idx}
                className="
                  border
                  rounded-xl
                  overflow-hidden
                  bg-white
                  shadow-sm
                "
              >

                <img
                  src={img.base64}
                  alt={img.name}
                  className="
                    w-full
                    h-40
                    object-cover
                  "
                />

                <div className="p-3 text-sm text-gray-700">
                  {img.name}
                </div>

              </div>

            ))}

          </div>

        ) : (

          <div className="text-gray-500 text-sm">
            No evidence attached
          </div>

        )}

      </div>

      {/* Compliance Signals */}

      <div className="mt-6">

        <div className="font-semibold mb-3">
          Compliance Signals
        </div>

        <div className="space-y-3">

          {complianceSignals.map((signal, idx) => (

            <div
              key={idx}
              className="
                border
                rounded-lg
                p-4
                bg-gray-50
              "
            >

              <div className="flex items-center justify-between">

                <div className="font-semibold text-sm">
                  AS9100 {signal.clause?.clause_id}
                </div>

                {signal.severity && (

                  <div
                    className="
                      text-xs
                      px-2
                      py-1
                      rounded-full
                      bg-yellow-100
                      text-yellow-700
                    "
                  >
                    {signal.severity}
                  </div>

                )}

              </div>

              <div className="space-y-2 mt-2">
                {(signal.gaps || []).map((gap, gapIdx) => (

                  <div
                    key={gapIdx}
                    className="
                      bg-red-50
                      border
                      border-red-200
                      rounded-lg
                      p-3
                      text-sm
                    "
                  >

                    <div className="font-medium text-red-700">
                      {GAP_TITLES[gap.type] || gap.type}
                    </div>

                    <div className="text-gray-700 mt-1">
                      {GAP_LABELS[gap.type] || gap.message}
                    </div>
                  </div>
                ))}

              </div>

              {signal.recommendation && (

                <div className="mt-3 text-sm text-blue-700">
                  Recommendation: {signal.recommendation}
                </div>

              )}

              {/* Keywords */}

              {(signal.clause?.matched_keywords || []).length > 0 && (

                <div className="flex flex-wrap gap-2 mt-3">

                  {signal.clause.matched_keywords.map(
                    (keyword, keywordIdx) => (

                      <div
                        key={keywordIdx}
                        className="
                          px-2
                          py-1
                          bg-blue-100
                          text-blue-700
                          rounded-full
                          text-xs
                        "
                      >
                        {keyword}
                      </div>

                    )
                  )}

                </div>

              )}

            </div>

          ))}

        </div>

      </div>

      {/* Checklist */}

      <div className="mt-6">

        <div className="font-semibold mb-2">
          Checklist Status
        </div>

        <div className="space-y-2">

          {checklist.required_fields.map((field, idx) => {

            const completed =
              checklist.completed_fields.includes(field);

            return (

              <div
                key={idx}
                className="flex items-center gap-3"
              >

                <div
                  className={`w-3 h-3 rounded-full ${
                    completed
                      ? "bg-green-500"
                      : "bg-red-500"
                  }`}
                />

                <div className="capitalize">
                  {field.replace("_", " ")}
                </div>

              </div>

            );

          })}

        </div>

      </div>

      {/* Suggested Actions */}

      <div className="mt-6">

        <div className="font-semibold mb-2">
          Suggested Actions
        </div>

        <div className="space-y-2">

          {suggestedActions.map((action, idx) => (

            <div
              key={idx}
              className="
                bg-yellow-50
                border
                border-yellow-200
                rounded-lg
                px-3
                py-2
                text-sm
              "
            >
              {action}
            </div>

          ))}

        </div>

      </div>

      {/* NCR Export */}

      <div className="mt-8">

        <button
          className="
            bg-black
            text-white
            px-5
            py-3
            rounded-lg
            hover:bg-gray-800
          "
          onClick={() => {
            const artifact = eventToArtifact(event);
            exportNcrDocument(artifact);
          }}
        >
          Generate NCR Draft
        </button>

      </div>

    </div>

  );
}